import { pb, pocketbaseUrl } from "../lib/pocketbase";

export const NG_COLLECTIONS = [
  "core_pharmacies",
  "core_staff_profiles",
  "core_modules",
  "home_widgets",
  "team_passations",
  "team_missions",
  "team_todos",
  "personal_notes",
  "patients_suivis",
  "rayon_demandes",
  "stock_commandes",
  "lab_suivis",
  "perfadom_dossiers",
  "quick_tools",
  "activity_feed",
  "notifications",
  "app_config",
];

const emptyCollections = () => Object.fromEntries(NG_COLLECTIONS.map((name) => [name, []]));

async function fetchWithTimeout(url, options = {}, timeoutMs = 7000) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal, cache: "no-store" });
  } finally {
    window.clearTimeout(timer);
  }
}

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export async function checkNgHealth() {
  if (!pocketbaseUrl) {
    return { connected: false, status: 0, message: "Variable VITE_POCKETBASE_URL absente" };
  }
  let lastResult = null;
  for (const delay of [0, 350, 900]) {
    if (delay) await wait(delay);
    try {
      const response = await fetchWithTimeout(`${pocketbaseUrl}/api/health`);
      lastResult = {
        connected: response.ok,
        status: response.status,
        message: response.ok ? "PocketBase NG répond" : `PocketBase NG répond avec le statut ${response.status}`,
      };
      if (response.ok) return lastResult;
      if (response.status >= 400 && response.status < 500) return lastResult;
    } catch (error) {
      lastResult = {
        connected: false,
        status: 0,
        message: error?.name === "AbortError" ? "Délai de connexion dépassé" : "PocketBase NG est inaccessible",
      };
    }
  }
  return lastResult;
}

async function readCollection(name) {
  const result = await pb.collection(name).getList(1, 100, { requestKey: null });
  return { name, items: result.items, totalItems: result.totalItems };
}

export async function loadNgHomeData() {
  const healthPromise = checkNgHealth();
  const reads = await Promise.allSettled(NG_COLLECTIONS.map(readCollection));
  const collections = emptyCollections();
  const counts = {};
  const errors = {};
  let successfulReads = 0;

  reads.forEach((result, index) => {
    const name = NG_COLLECTIONS[index];
    if (result.status === "fulfilled") {
      collections[name] = result.value.items;
      counts[name] = result.value.totalItems;
      successfulReads += 1;
    } else {
      counts[name] = null;
      errors[name] = result.reason?.message || "Lecture impossible";
    }
  });

  const health = await healthPromise;
  return {
    collections,
    counts,
    errors,
    health,
    connected: health.connected || successfulReads > 0,
    successfulReads,
    loadedAt: new Date(),
  };
}

export async function runNgDiagnostic() {
  const health = await checkNgHealth();
  const targetCollections = ["core_pharmacies", "core_modules", "quick_tools"];
  const results = await Promise.allSettled(targetCollections.map(readCollection));
  const counts = Object.fromEntries(targetCollections.map((name, index) => [
    name,
    results[index].status === "fulfilled" ? results[index].value.totalItems : null,
  ]));
  const successfulReads = results.filter((result) => result.status === "fulfilled").length;
  return {
    url: pocketbaseUrl || "Non configurée",
    health: successfulReads > 0 && !health.connected
      ? { connected: true, status: health.status, message: "PocketBase NG répond aux lectures" }
      : health,
    counts,
    testedAt: new Date(),
  };
}

export async function runNgStatus() {
  return {
    url: pocketbaseUrl || "Non configurée",
    health: await checkNgHealth(),
    counts: {},
    testedAt: new Date(),
  };
}
